/**
 * How many multiview tiles the Android app allows (owner decision 2026-09-16):
 * up to 4, capped by what the device can decode, the provider's connection
 * limit, the streams the viewer's other devices hold, and recordings in progress
 * — a recording always wins over a tile. The website keeps its own cap (the
 * server's stream gate), so none of this applies there.
 *
 * Pure, for tests; MultiviewOverlay feeds it.
 */

/** Hard cap on simultaneous tiles, everywhere. */
export const MAX_MULTIVIEW_TILES = 4;

/** The provider's side, from the DVR plugin's `connections` (Xtream account info). */
export interface StreamBudget {
  /** 0 when unknown (an M3U source has no account info). */
  maxConnections: number;
  /** Streams open on the account right now, all devices; -1 when unknown. */
  activeConnections: number;
  /** Recordings running on this device. */
  recordingNow: number;
}

/**
 * Streams the viewer's OTHER devices hold, read once as multiview opens.
 * `ownOpen` is what this device had open when the provider was asked: the
 * channel multiview started from, plus its recordings. Providers update the count
 * with a lag, so this is a best guess; a tile the provider still refuses says so.
 */
export function otherDeviceStreams(budget: StreamBudget, ownOpen: number): number {
  if (budget.activeConnections < 0) return 0;
  return Math.max(0, budget.activeConnections - ownOpen);
}

export type TileLimitReason = "device" | "provider" | "other_devices" | "recording";

export interface TileLimit {
  limit: number;
  /** Why the grid stops below 4; null when it does not. */
  reason: TileLimitReason | null;
}

export function tileLimit(input: {
  deviceTiles: number;
  maxConnections: number;
  recordingNow: number;
  otherDevices: number;
}): TileLimit {
  let limit = Math.min(MAX_MULTIVIEW_TILES, Math.max(1, Math.floor(input.deviceTiles)));
  let reason: TileLimitReason | null = limit < MAX_MULTIVIEW_TILES ? "device" : null;
  if (input.maxConnections > 0) {
    const free = input.maxConnections - input.recordingNow - input.otherDevices;
    if (free < limit) {
      limit = free;
      reason = input.recordingNow > 0 ? "recording" : input.otherDevices > 0 ? "other_devices" : "provider";
    }
  }
  // The channel multiview opened with always stays: it was already playing.
  return { limit: Math.max(1, limit), reason: Math.max(1, limit) < MAX_MULTIVIEW_TILES ? reason : null };
}

/** The picker's note when the grid is full, in the viewer's terms. */
export function tileLimitMessage(limit: TileLimit, input: { maxConnections: number; recordingNow: number; otherDevices: number }): string {
  const n = limit.limit;
  const channels = `${n} channel${n === 1 ? "" : "s"}`;
  const streams = `${input.maxConnections} stream${input.maxConnections === 1 ? "" : "s"}`;
  switch (limit.reason) {
    case "device":
      return `This device can play ${channels} at once`;
    case "recording":
      return `Your provider allows ${streams} at a time and ${input.recordingNow === 1 ? "a recording is" : `${input.recordingNow} recordings are`} using ${input.recordingNow === 1 ? "one" : "some"} — grid full at ${channels}`;
    case "other_devices":
      return `Your provider allows ${streams} at a time and ${input.otherDevices} ${input.otherDevices === 1 ? "is" : "are"} in use on another device — grid full at ${channels}`;
    case "provider":
      return `Your provider allows ${streams} at a time`;
    default:
      return "Grid full — remove a tile to add more";
  }
}
